<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, inject, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import moment from "moment"
import { useSourceStore } from "@core/stores/source.store"
import { useAuthStore } from "@core/stores/auth.store"
import { useConfigStore } from "@core/stores/config.store"
import { METER_TYPES_LIST, SOURCES_TYPES } from "@core/constants"
import { useTaskStore } from "@core/stores/task.store"
import type { DataVariable, Source } from "@types"
import type { Services } from "@services"

// ================================
// PROPS & MODEL
// ================================

const props = defineProps<{
    _id_source: string
}>()

const visible = defineModel<boolean>("visible", {
    required: true,
    default: false,
})

// ================================
// INTERNATIONALIZATION
// ================================

const { t } = useI18n({
    messages: {
        fr: {
            title: "Exporter les données",
            question: "Spécifiez les options d'export",
            type_export: {
                label: "Type d'export",
                placeholder: "Choisir un type d'export",
                export_data: "Export des données (Excel)",
                export_r63: "Export R63 (PDL)",
            },
            period: {
                label: "Période",
                start_date: "Date de début",
                end_date: "Date de fin",
                error: "La date de début doit être antérieure à la date de fin.",
            },
            variables: {
                label: "Variables à exporter",
                no_variables: "Aucune variable disponible pour cette source.",
                loading_variables: "Chargement des variables...",
                hint: "Sélectionnez une ou plusieurs variables",
                all: "Toutes",
                error: "Sélectionnez au moins une variable.",
                cost: "Coût",
                ambient: "Conditions ambiantes",
                volume: "Volume",
                energy_consumption: "Energie/Consommation",
                power: "Puissance",
                coefficient: "Coefficient",
                other: "Autre",
            },
            freqs: {
                label: "Fréquences",
            },
            freq_labels: {
                raw: "Brut/Agrégée",
                hourly: "Horaire",
                daily: "Journalier",
                weekly: "Hebdomadaire",
                monthly: "Mensuel",
            },
            variables_meta: {
                freqs_unknown:
                    "Périodes non renseignées dans les métadonnées de la source.",
                freqs_empty: "Aucune fréquence avec données disponibles.",
                freqs_hint: "Fréquences avec données : {list}",
            },
            freq_conflict: {
                intro: "Certaines variables n'ont pas de données pour toutes les fréquences sélectionnées.",
                col_variable: "Variable",
                col_allowed: "Fréquences avec données",
                col_invalid: "Sélection non couverte",
                footer: "Retirez une de ces fréquences ou désélectionnez la variable concernée.",
                see_problematic: "Voir les variables problématiques",
                detail_title: "Détail — variables et fréquences",
            },
            submit: "Générer l'export",
            loading: "Génération en cours...",
            task_export_title: "Export des données",
            task_export_success: "L'export est prêt. Le fichier va s'ouvrir.",
            task_export_failure: "L'export a échoué.",
        },
    },
})

// ================================
// STORES & SERVICES
// ================================

const sourceStore = useSourceStore()
const authStore = useAuthStore()
const configStore = useConfigStore()
const taskStore = useTaskStore()
const services = inject<Services>("services")

// ================================
// STATE
// ================================

const exportTypeSelected = ref<string>("export-data")
const startDate = ref<Date>(moment().subtract(3, "month").toDate())
const endDate = ref<Date>(moment().toDate())
const variablesSelected = ref<string[]>([])
const freqsSelected = ref<string[]>([
    "raw",
    "hourly",
    "daily",
    "weekly",
    "monthly",
])
const loading = ref(false)
const errorMessage = ref("")

/** Sous-dialog : détail du warning variables / fréquences (tableau). */
const visibleFreqConflictDetail = ref(false)

// ================================
// COMPUTED
// ================================

/** Source chargée depuis le store. */
const currentSource = computed(() =>
    props._id_source ? sourceStore.getSourceById(props._id_source) : undefined
)

/** Clé `informations` pour lire `_variables` (même logique que source.store). */
function getInformationsKeyForVariables(source: Source): string {
    let type = source.type
    if (![...METER_TYPES_LIST, "meteo"].includes(type)) {
        type = "iot"
    }
    return type
}

/** Indique si une période a date_from et date_to renseignés. */
function hasDataPeriod(
    period: { date_from: Date | null; date_to: Date | null } | undefined | null
): boolean {
    return !!(period?.date_from && period?.date_to)
}

/** Fréquences d'export autorisées d'après les métadonnées `_variables`. */
function getAllowedFreqValuesFromDataVariable(dv: DataVariable): string[] {
    const out: string[] = []
    if (hasDataPeriod(dv.raw) || hasDataPeriod(dv.raw_periode)) {
        out.push("raw")
    }
    if (hasDataPeriod(dv.hourly)) out.push("hourly")
    if (hasDataPeriod(dv.daily)) out.push("daily")
    if (hasDataPeriod(dv.weekly)) out.push("weekly")
    if (hasDataPeriod(dv.monthly)) out.push("monthly")
    return out
}

/** Métadonnées `_variables` de la source (périodes par fréquence). */
const getSourceDataVariables = computed((): DataVariable[] => {
    const source = currentSource.value
    if (!source) return []
    const key = getInformationsKeyForVariables(source)
    const informations = source.informations as Record<string, unknown>
    const block = informations[key] as
        | { data?: { _variables?: DataVariable[] } }
        | undefined
    return block?.data?._variables ?? []
})

/** Index type → entrée `_variables`. */
const getDataVariableByType = computed(() => {
    const m = new Map<string, DataVariable>()
    for (const v of getSourceDataVariables.value) {
        m.set(v.type, v)
    }
    return m
})

/** Liste des types de variables disponibles pour la source. */
const getVariableTypes = computed(() =>
    sourceStore.getVariablesBySourceId(props._id_source)
)

/** Indique si on a besoin de la config variables (export données + source a des types). */
const getNeedVariablesConfig = computed(
    () =>
        exportTypeSelected.value === "export-data" &&
        getVariableTypes.value.length > 0
)

/**
 * Indique si les variables de config sont en cours de chargement.
 * Vrai quand on en a besoin, que la config est vide et que le fetch est en cours.
 */
const getIsVariablesConfigLoading = computed(
    () =>
        getNeedVariablesConfig.value &&
        configStore.getVariables.length === 0 &&
        configStore.isLoadingVariables
)

/**
 * Liste des variables au format "type.stat" en faisant correspondre chaque type
 * avec la variable de configuration (config.store) pour récupérer le stat associé.
 * Si la variable n'est pas trouvée en config, fallback sur "type.sum".
 */
const getVariableWithStatList = computed(() => {
    return getVariableTypes.value.map((type) => {
        const configVariable = configStore.getVariableByType(type)
        const stat = configVariable?.stat ?? "sum"
        return `${type}.${stat}`
    })
})

/**
 * Options pour le multi-select : label = nom config ou type, value = "type.stat",
 * fréquences autorisées dérivées de `_variables` (date_from + date_to renseignés).
 */
const getVariableOptions = computed(() =>
    getVariableTypes.value.map((type) => {
        const configVariable = configStore.getVariableByType(type)
        const stat = configVariable?.stat ?? "sum"
        const value = `${type}.${stat}`
        const label = configVariable?.name ?? type
        const dv = getDataVariableByType.value.get(type)
        let allowedFreqs: string[] | null
        let freqsHint: string
        if (dv === undefined) {
            allowedFreqs = null
            freqsHint = t("variables_meta.freqs_unknown")
        } else {
            allowedFreqs = getAllowedFreqValuesFromDataVariable(dv)
            if (allowedFreqs.length === 0) {
                freqsHint = t("variables_meta.freqs_empty")
            } else {
                const list = allowedFreqs
                    .map((f) => t(`freq_labels.${f}`))
                    .join(", ")
                freqsHint = t("variables_meta.freqs_hint", { list })
            }
        }
        return { label, value, freqsHint, allowedFreqs }
    })
)

/** Accès rapide par valeur `type.stat` (validation fréquences). */
const getVariableOptionByValue = computed(() => {
    const m = new Map<
        string,
        {
            label: string
            value: string
            freqsHint: string
            allowedFreqs: string[] | null
        }
    >()
    for (const o of getVariableOptions.value) {
        m.set(o.value, o)
    }
    return m
})

/**
 * Variables sélectionnées pour lesquelles au moins une fréquence choisie
 * n'est pas couverte par les métadonnées.
 */
const getVariableFreqConflicts = computed(() => {
    const rows: {
        value: string
        name: string
        allowed: string
        invalid: string
    }[] = []
    for (const value of variablesSelected.value) {
        const opt = getVariableOptionByValue.value.get(value)
        if (!opt || opt.allowedFreqs === null) continue
        const allowedSet = new Set(opt.allowedFreqs)
        const invalidFreqs = freqsSelected.value.filter(
            (f) => !allowedSet.has(f)
        )
        if (invalidFreqs.length === 0) continue
        rows.push({
            value,
            name: opt.label,
            allowed: opt.allowedFreqs
                .map((f) => t(`freq_labels.${f}`))
                .join(", "),
            invalid: invalidFreqs.map((f) => t(`freq_labels.${f}`)).join(", "),
        })
    }
    return rows
})

/**
 * Catégorie d'affichage d'une variable selon le préfixe de son libellé.
 */
function getVariableCategoryFromLabel(
    label: string
):
    | "cost"
    | "ambient"
    | "volume"
    | "energy_consumption"
    | "power"
    | "coefficient"
    | "other" {
    if (label.startsWith("Coût")) return "cost"
    const ambientPrefixes = ["Température", "Humidité", "CO2"] as const
    for (const prefix of ambientPrefixes) {
        if (label.startsWith(prefix)) return "ambient"
    }
    if (label.startsWith("Volume")) return "volume"
    if (
        label.startsWith("Energie") ||
        label.startsWith("Énergie") ||
        label.startsWith("Consommation")
    ) {
        return "energy_consumption"
    }
    if (label.startsWith("Puissance")) return "power"
    if (label.startsWith("Coefficient")) return "coefficient"
    return "other"
}

/** Ordre d'affichage des groupes dans le sélecteur. */
const VARIABLE_CATEGORY_ORDER: (
    | "cost"
    | "ambient"
    | "volume"
    | "energy_consumption"
    | "power"
    | "coefficient"
    | "other"
)[] = [
    "cost",
    "ambient",
    "volume",
    "energy_consumption",
    "power",
    "coefficient",
    "other",
]

/**
 * Options des variables regroupées par catégorie (libellé affiché).
 */
const getVariableOptionsGrouped = computed(() => {
    const options = getVariableOptions.value
    const byCategory = new Map<
        | "cost"
        | "ambient"
        | "volume"
        | "energy_consumption"
        | "power"
        | "coefficient"
        | "other",
        { label: string; value: string; freqsHint: string }[]
    >()
    for (const opt of options) {
        const category = getVariableCategoryFromLabel(opt.label)
        const list = byCategory.get(category) ?? []
        list.push({
            label: opt.label,
            value: opt.value,
            freqsHint: opt.freqsHint,
        })
        byCategory.set(category, list)
    }
    return VARIABLE_CATEGORY_ORDER.filter(
        (cat) => (byCategory.get(cat)?.length ?? 0) > 0
    ).map((category) => ({
        label: t(`variables.${category}`),
        items: byCategory.get(category) ?? [],
    }))
})

/** Liste des types d'export (export-data toujours ; export-r63 si PDL). */
const getListExports = computed(() => {
    const list: { value: string; label: string }[] = [
        { value: "export-data", label: t("type_export.export_data") },
    ]
    if (currentSource.value?.type === SOURCES_TYPES.PDL) {
        list.push({ value: "export-r63", label: t("type_export.export_r63") })
    }
    return list
})

/** Options des fréquences (toutes par défaut). */
const getFreqOptions = computed(() => [
    { value: "raw", label: t("freq_labels.raw") },
    { value: "hourly", label: t("freq_labels.hourly") },
    { value: "daily", label: t("freq_labels.daily") },
    { value: "weekly", label: t("freq_labels.weekly") },
    { value: "monthly", label: t("freq_labels.monthly") },
])

/** Validation des dates (début <= fin). */
const getIsValidDates = computed(() =>
    moment(startDate.value).isSameOrBefore(moment(endDate.value))
)

/** Formulaire valide selon le type d'export. */
const getIsFormValid = computed(() => {
    if (!exportTypeSelected.value) return false
    if (!getIsValidDates.value) return false
    if (!freqsSelected.value.length) return false
    if (exportTypeSelected.value === "export-data") {
        if (getIsVariablesConfigLoading.value) return false
        if (variablesSelected.value.length === 0) return false
        return true
    }
    return true
})

// ================================
// WATCHERS
// ================================

/** Réinitialise les variables sélectionnées (format "type.stat") quand la liste change. */
watch(
    () => getVariableWithStatList.value,
    (variableWithStatList) => {
        variablesSelected.value =
            variableWithStatList.length > 0 ? [...variableWithStatList] : []
    },
    { immediate: true }
)

/** Quand on a besoin des variables config et qu'elles sont vides, on les charge. */
watch(
    () =>
        [
            visible.value,
            exportTypeSelected.value,
            getVariableTypes.value.length,
            configStore.getVariables.length,
        ] as const,
    ([vis, exportType, typesLength, configLength]) => {
        if (
            vis &&
            exportType === "export-data" &&
            typesLength > 0 &&
            configLength === 0
        ) {
            configStore.fetchVariables()
        }
    },
    { immediate: true }
)

/** Ferme le sous-dialog de détail à la fermeture de l’export. */
watch(visible, (v) => {
    if (!v) visibleFreqConflictDetail.value = false
})

/** Ferme le détail des conflits quand il n’y en a plus. */
watch(
    () => getVariableFreqConflicts.value.length,
    (len) => {
        if (len === 0) visibleFreqConflictDetail.value = false
    }
)

// ================================
// INTERACTIONS
// ================================

/**
 * Soumet le formulaire : appelle l'API d'export puis ouvre file_url.
 */
async function onSubmitForm() {
    if (!getIsFormValid.value || !services) return
    if (
        exportTypeSelected.value === "export-data" &&
        variablesSelected.value.length === 0
    ) {
        return
    }
    errorMessage.value = ""
    loading.value = true

    try {
        const dateFromStr = moment(startDate.value).format("YYYY-MM-DD")
        const dateToStr = moment(endDate.value).format("YYYY-MM-DD")

        if (exportTypeSelected.value === "export-r63") {
            const file_url = await services.$exportR63({
                _id_source: props._id_source,
                date_from: dateFromStr,
                date_to: dateToStr,
            })
            if (file_url) {
                window.location.assign(file_url)
                visible.value = false
            }
        } else {
            const _id_user = authStore.userId || ""
            const outcome = await services.$exportSource({
                _id_source: props._id_source,
                date_from: dateFromStr,
                date_to: dateToStr,
                variables: variablesSelected.value,
                freqs: freqsSelected.value,
                _id_user,
            })
            if (outcome.mode === "sync") {
                window.location.assign(outcome.fileUrl)
                visible.value = false
            } else {
                taskStore.followTask(outcome.taskId, {
                    taskname: "task_route_async",
                    taskToastTitle: t("task_export_title"),
                    taskToastSuccessMessage: t("task_export_success"),
                    taskToastFailureMessage: t("task_export_failure"),
                    notification: true,
                    autoDelete: true,
                })
                visible.value = false
            }
        }
    } catch (err) {
        errorMessage.value =
            err instanceof Error ? err.message : "Erreur lors de l'export"
    } finally {
        loading.value = false
    }
}

/** Initialise les dates par défaut à l'ouverture. */
function onOpen() {
    startDate.value = moment().subtract(3, "month").toDate()
    endDate.value = moment().toDate()
    exportTypeSelected.value = "export-data"
    errorMessage.value = ""
    visibleFreqConflictDetail.value = false
}
</script>

<template>
    <BaseDialog
        v-model:visible="visible"
        modal
        closable
        dismissable-mask
        position="top"
        class="export-source-dialog max-w-[32rem] w-full"
        :header="t('title')"
        :style="{ width: '100%' }"
        :breakpoints="{ '575px': '90vw' }"
        @show="onOpen"
    >
        <p class="text-surface-600 text-sm mb-4">
            {{ t("question") }}
        </p>

        <form
            class="export-source-dialog__form flex flex-col gap-5"
            @submit.prevent="onSubmitForm"
        >
            <!-- Type d'export -->
            <div class="flex flex-col gap-2">
                <label class="text-sm font-medium text-surface-700">
                    {{ t("type_export.label") }}
                </label>
                <BaseSelect
                    v-model="exportTypeSelected"
                    :options="getListExports"
                    option-label="label"
                    option-value="value"
                    :placeholder="t('type_export.placeholder')"
                    :disabled="loading"
                    class="w-full"
                />
            </div>

            <!-- Période -->
            <div v-if="exportTypeSelected" class="flex flex-col gap-2">
                <label class="text-sm font-medium text-surface-700">
                    {{ t("period.label") }}
                </label>
                <div class="flex flex-wrap gap-4">
                    <BaseInputWrapper
                        input-id="export-date-from"
                        :label="t('period.start_date')"
                        :error="
                            !getIsValidDates ? t('period.error') : undefined
                        "
                    >
                        <BaseDatePicker
                            id="export-date-from"
                            v-model="startDate"
                            date-format="dd/mm/yy"
                            :max-date="endDate"
                            :invalid="!getIsValidDates"
                            class="w-full"
                        />
                    </BaseInputWrapper>
                    <BaseInputWrapper
                        input-id="export-date-to"
                        :label="t('period.end_date')"
                        :error="
                            !getIsValidDates ? t('period.error') : undefined
                        "
                    >
                        <BaseDatePicker
                            id="export-date-to"
                            v-model="endDate"
                            date-format="dd/mm/yy"
                            :invalid="!getIsValidDates"
                            class="w-full"
                        />
                    </BaseInputWrapper>
                </div>
            </div>

            <!-- Variables (export données uniquement) -->
            <div
                v-if="exportTypeSelected === 'export-data'"
                class="flex flex-col gap-2"
            >
                <label class="text-sm font-medium text-surface-700">
                    {{ t("variables.label") }}
                </label>
                <div
                    v-if="getIsVariablesConfigLoading"
                    class="flex items-center gap-2 text-surface-500 text-sm py-2"
                >
                    <i class="pi pi-spin pi-spinner" aria-hidden="true" />
                    <span>{{ t("variables.loading_variables") }}</span>
                </div>
                <div
                    v-else-if="getVariableTypes.length === 0"
                    class="text-warn text-sm"
                >
                    {{ t("variables.no_variables") }}
                </div>
                <BaseMultiSelect
                    v-else
                    v-model="variablesSelected"
                    :options="getVariableOptionsGrouped"
                    option-label="label"
                    option-value="value"
                    option-group-label="label"
                    option-group-children="items"
                    :placeholder="t('variables.hint')"
                    filter
                    :disabled="loading"
                    class="w-full"
                >
                    <template #option="slotProps">
                        <div class="flex flex-col gap-0.5 py-0.5">
                            <span>{{ slotProps.option.label }}</span>
                            <span class="text-xs text-surface-500">{{
                                slotProps.option.freqsHint
                            }}</span>
                        </div>
                    </template>
                </BaseMultiSelect>
            </div>

            <!-- Fréquences (export données uniquement) -->
            <div
                v-if="exportTypeSelected === 'export-data'"
                class="flex flex-col gap-2"
            >
                <label class="text-sm font-medium text-surface-700">
                    {{ t("freqs.label") }}
                </label>
                <BaseMultiSelect
                    v-model="freqsSelected"
                    :options="getFreqOptions"
                    option-label="label"
                    option-value="value"
                    :disabled="loading"
                    class="w-full"
                />
            </div>

            <BaseMessage v-if="errorMessage" severity="error" class="mt-2">
                {{ errorMessage }}
            </BaseMessage>

            <BaseMessage
                v-if="
                    exportTypeSelected === 'export-data' &&
                    getVariableFreqConflicts.length > 0
                "
                severity="warn"
                class="mt-2"
                :closable="false"
            >
                <div class="flex flex-col gap-2">
                    <p class="text-sm leading-relaxed text-surface-700">
                        {{ t("freq_conflict.intro") }}
                    </p>
                    <button
                        type="button"
                        class="text-sm text-primary self-start underline-offset-2 hover:underline rounded focus-visible:outline-2 focus-visible:outline-offset-2"
                        @click="visibleFreqConflictDetail = true"
                    >
                        {{ t("freq_conflict.see_problematic") }}
                    </button>
                </div>
            </BaseMessage>

            <BaseButton
                v-if="exportTypeSelected"
                type="submit"
                :label="loading ? t('loading') : t('submit')"
                :disabled="!getIsFormValid || loading"
                :loading="loading"
                class="w-full mt-2"
            />
        </form>
    </BaseDialog>

    <BaseDialog
        v-model:visible="visibleFreqConflictDetail"
        modal
        closable
        dismissable-mask
        position="top"
        class="export-source-dialog__freq-conflict-detail max-w-4xl w-full"
        :header="t('freq_conflict.detail_title')"
        :style="{ width: '100%' }"
        :breakpoints="{ '575px': '90vw' }"
    >
        <div class="max-h-[min(70vh,28rem)] overflow-y-auto text-surface-700">
            <p class="text-sm leading-relaxed font-medium text-surface-800">
                {{ t("freq_conflict.intro") }}
            </p>
            <div
                class="mt-4 overflow-x-auto rounded-lg border border-surface-200"
            >
                <table class="w-full text-sm text-left border-collapse">
                    <thead>
                        <tr class="bg-surface-50 border-b border-surface-200">
                            <th
                                scope="col"
                                class="py-3 px-3 font-medium text-surface-600"
                            >
                                {{ t("freq_conflict.col_variable") }}
                            </th>
                            <th
                                scope="col"
                                class="py-3 px-3 font-medium text-surface-600"
                            >
                                {{ t("freq_conflict.col_allowed") }}
                            </th>
                            <th
                                scope="col"
                                class="py-3 px-3 font-medium text-surface-600"
                            >
                                {{ t("freq_conflict.col_invalid") }}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="row in getVariableFreqConflicts"
                            :key="row.value"
                            class="border-b border-surface-100 last:border-0 align-top odd:bg-surface-0 even:bg-surface-50/60"
                        >
                            <td
                                class="py-3 px-3 font-semibold text-surface-900"
                            >
                                {{ row.name }}
                            </td>
                            <td class="py-3 px-3 text-surface-700">
                                {{ row.allowed }}
                            </td>
                            <td class="py-3 px-3 text-surface-700">
                                {{ row.invalid }}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <p class="text-sm leading-relaxed text-surface-600 mt-4">
                {{ t("freq_conflict.footer") }}
            </p>
        </div>
    </BaseDialog>
</template>
